import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table, TableHead, TableRow, TableCell, TableBody, Link, IconButton,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import { useSelector } from 'react-redux';
import { formatSpeed, formatTime } from '../common/util/formatter';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { prefixString, unprefixString } from '../common/util/stringUtils';
import { useTranslation, useTranslationKeys } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import usePersistedState from '../common/util/usePersistedState';
import ColumnSelect from './components/ColumnSelect';
import { useCatch, useEffectAsync } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import { useAttributePreference } from '../common/util/preferences';
import MapPositions from '../map/MapPositions';
import scheduleReport from './common/scheduleReport';
import ReportMapPanel from './components/ReportMapPanel';
import ReportMapContent from './components/ReportMapContent';
import SelectField from '../common/components/SelectField';
import fetchOrThrow from '../common/util/fetchOrThrow';
import exportExcel from '../common/util/exportExcel';
import AddressValue from '../common/components/AddressValue';
import { useTheme } from '@mui/material/styles';

const columnsArray = [
  ['eventTime', 'positionFixTime'],
  ['type', 'sharedType'],
  ['geofenceId', 'sharedGeofence'],
  ['maintenanceId', 'sharedMaintenance'],
  ['address', 'positionAddress'],
  ['attributes', 'commandData'],
];
const columnsMap = new Map(columnsArray);

const EventReportPage = () => {
  const navigate = useNavigate();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  const devices = useSelector((state) => state.devices.items);
  const geofences = useSelector((state) => state.geofences.items);

  const speedUnit = useAttributePreference('speedUnit');

  const [allEventTypes, setAllEventTypes] = useState([['allEvents', 'eventAll']]);

  const alarms = useTranslationKeys((it) => it.startsWith('alarm')).map((it) => ({
    key: unprefixString('alarm', it),
    name: t(it),
  }));

  const [columns, setColumns] = usePersistedState('eventColumns', ['eventTime', 'type', 'address', 'attributes']);
  const eventTypes = useMemo(() => searchParams.getAll('eventType'), [searchParams]);
  const alarmTypes = useMemo(() => searchParams.getAll('alarmType'), [searchParams]);
  const [items, setItems] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!eventTypes.length) {
      updateReportParams(searchParams, setSearchParams, 'eventType', ['allEvents']);
    }
  }, [searchParams, setSearchParams, eventTypes])

  useEffect(() => {
    if (selectedItem?.positionId) {
      setPosition(positions[selectedItem.positionId] || null);
    } else {
      setPosition(null);
    }
  }, [selectedItem, positions]);

  useEffectAsync(async () => {
    const response = await fetchOrThrow('/api/notifications/types');
    const types = await response.json();
    setAllEventTypes([...allEventTypes, ...types.map((it) => [it.type, prefixString('event', it.type)])]);
  }, []);

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    eventTypes.forEach((it) => query.append('type', it));
    if (eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm')) {
      alarmTypes.forEach((it) => query.append('alarm', it));
    }
    setSelectedItem(null);
    setPosition(null);
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/events?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const events = await response.json();
      setItems(events);
      const positionIds = Array.from(new Set(events
        .map((event) => event.positionId)
        .filter((id) => id)));
      const positionsMap = {};
      if (positionIds.length > 0) {
        const positionsQuery = new URLSearchParams();
        positionIds.slice(0, 128).forEach((id) => positionsQuery.append('id', id));
        const positionsResponse = await fetchOrThrow(`/api/positions?${positionsQuery.toString()}`);
        const positionsArray = await positionsResponse.json();
        positionsArray.forEach((p) => positionsMap[p.id] = p);
      }
      setPositions(positionsMap);
    } finally {
      setLoading(false);
    }
  });

  const onExport = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    eventTypes.forEach((it) => query.append('type', it));
    if (eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm')) {
      alarmTypes.forEach((it) => query.append('alarm', it));
    }

    const response = await fetchOrThrow(`/api/reports/events?${query.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    const events = await response.json();
    const positionIds = Array.from(new Set(events
      .map((event) => event.positionId)
      .filter((id) => id)));
    const positionsMap = {};
    if (positionIds.length > 0) {
      const positionsQuery = new URLSearchParams();
      positionIds.slice(0, 128).forEach((id) => positionsQuery.append('id', id));
      const positionsResponse = await fetchOrThrow(`/api/positions?${positionsQuery.toString()}`);
      const positionsArray = await positionsResponse.json();
      positionsArray.forEach((p) => { positionsMap[p.id] = p; });
    }

    const formatExportValue = (item, key) => {
      if (key === 'address') {
        return positionsMap[item.positionId]?.address || '';
      }
      if (key === 'attributes') {
        if (item.type === 'alarm') {
          return t(prefixString('alarm', item.attributes.alarm));
        }
        if (item.type === 'deviceOverspeed') {
          return formatSpeed(item.attributes.speed, speedUnit, t);
        }
        if (item.type === 'driverChanged') {
          return item.attributes.driverUniqueId || '';
        }
        if (item.type === 'media') {
          return item.attributes.file || '';
        }
        if (item.type === 'commandResult') {
          return item.attributes.result || '';
        }
        return '';
      }
      return formatValue(item, key);
    };

    const sheetRows = events.map((item) => {
      const row = {
        [t('sharedDevice')]: devices[item.deviceId]?.name || item.deviceId,
      };
      columns.forEach((key) => {
        row[t(columnsMap.get(key))] = formatExportValue(item, key);
      });
      return row;
    });

    await exportExcel(t('reportEvents'), 'events.xlsx', new Map([[t('reportEvents'), sheetRows]]), theme);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'events';
    if (eventTypes[0] !== 'allEvents') {
      report.attributes.types = eventTypes.join(',');
    }
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value].name;
      case 'eventTime':
        return formatTime(value, 'seconds');
      case 'type':
        return t(prefixString('event', value));
      case 'geofenceId':
        if (value > 0) {
          const geofence = geofences[value];
          return geofence && geofence.name;
        }
        return null;
      case 'maintenanceId':
        return value > 0 ? value : null;
      case 'address': {
        const position = positions[item.positionId];
        if (position) {
          return (
            <AddressValue
              latitude={position.latitude}
              longitude={position.longitude}
              originalAddress={position.address}
            />
          );
        }
        return '';
      }
      case 'attributes':
        switch (item.type) {
          case 'alarm':
            return t(prefixString('alarm', item.attributes.alarm));
          case 'deviceOverspeed':
            return formatSpeed(item.attributes.speed, speedUnit, t);
          case 'driverChanged':
            return item.attributes.driverUniqueId;
          case 'media':
            return (<Link href={`/api/media/${devices[item.deviceId]?.uniqueId}/${item.attributes.file}`} target="_blank">{item.attributes.file}</Link>);
          case 'commandResult':
            return item.attributes.result;
          default:
            return '';
        }
      default:
        return value;
    }
  };

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportEvents']}>
      <div className={classes.container}>
        <ReportMapPanel
          title={t('reportEvents')}
          show={Boolean(selectedItem)}
          onClearSelection={selectedItem ? () => setSelectedItem(null) : null}
          onExpandedChange={setMapExpanded}
        >
          <ReportMapContent
            camera={Boolean(position)}
            cameraProps={position ? { latitude: position.latitude, longitude: position.longitude } : {}}
          >
            {position && <MapPositions positions={[position]} titleField="fixTime" />}
          </ReportMapContent>
        </ReportMapPanel>
        <div
          className={classes.containerMain}
          data-has-map={Boolean(selectedItem)}
          data-map-expanded={mapExpanded}
        >
          <div className={classes.header}>
            <ReportFilter onShow={onShow} onExport={onExport} onSchedule={onSchedule} deviceType="multiple" loading={loading}>
              <div className={classes.filterItem}>
                <SelectField
                  label={t('reportEventTypes')}
                  value={eventTypes}
                  onChange={(e) => {
                    const values = e.target.value;
                    const hadAllEvents = eventTypes.includes('allEvents');
                    const hasAllEvents = values.includes('allEvents');
                    let normalized = values;
                    if (hasAllEvents && values.length > 1) {
                      normalized = hadAllEvents ? values.filter((value) => value !== 'allEvents') : ['allEvents'];
                    }
                    updateReportParams(searchParams, setSearchParams, 'eventType', normalized);
                  }}
                  multiple
                  fullWidth
                  searchPlaceholder={t('sharedSearch')}
                  data={allEventTypes.map(([key, string]) => ({ key, title: t(string) }))}
                  keyGetter={(item) => item.key}
                  titleGetter={(item) => item.title}
                />
              </div>
              {eventTypes[0] !== 'allEvents' && eventTypes.includes('alarm') && (
                <div className={classes.filterItem}>
                  <SelectField
                    multiple
                    value={alarmTypes}
                    onChange={(e) => updateReportParams(searchParams, setSearchParams, 'alarmType', e.target.value)}
                    data={alarms}
                    keyGetter={(it) => it.key}
                    label={t('sharedAlarms')}
                    fullWidth
                  />
                </div>
              )}
              <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
            </ReportFilter>
          </div>
          <div className={classes.tableCard}>
            <div className={classes.tableScroller} data-has-map={Boolean(selectedItem)} data-map-expanded={mapExpanded}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.columnAction} />
                    <TableCell>{t('sharedDevice')}</TableCell>
                    {columns.map((key) => (<TableCell key={key}>{t(columnsMap.get(key))}</TableCell>))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading ? (items.length ? items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className={classes.columnAction} padding="none">
                        {(item.positionId && (selectedItem === item ? (
                          <IconButton size="small" onClick={() => setSelectedItem(null)}>
                            <GpsFixedIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => setSelectedItem(item)}>
                            <LocationSearchingIcon fontSize="small" />
                          </IconButton>
                        ))) || ''}
                      </TableCell>
                      <TableCell>{devices[item.deviceId].name}</TableCell>
                      {columns.map((key) => (
                        <TableCell key={key}>
                          {formatValue(item, key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={columns.length + 2}>{t('reportNoData')}</TableCell>
                    </TableRow>
                  )) : (<TableShimmer columns={columns.length + 2} />)}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default EventReportPage;
